import { Dropdown, Menu, Popconfirm } from "antd";
import Table, { ColumnsType } from "antd/es/table";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { routePath } from "config/routeMgt/routePath";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  QUERY_KEY_FOR_SINGLE_FOLDER,
  useGetSingleFolder,
} from "../hooks/useGetSingleFolder";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { IAddTalent, talentList } from "../types";
import userDp from "assets/images/placeholderImage.jpeg";
import { openNotification } from "utils/notification";
import { useQueryClient } from "react-query";
import { LoadingOutlined } from "@ant-design/icons";
import { useAddAndRemoveTalent } from "../hooks/useAddAndRemoveTalent";
import { SendInvite } from "features/talents/components/SendInvite";
import { FilterFolder } from "../components/FilterFolder";
import { useGlobalContext } from "stateManagers/GlobalContext";

export const SingleFolder = () => {
  const { filterFolderState } = useGlobalContext();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [inviteModal, setInviteModal] = useState(false);
  const [talentId, setTalentId] = useState<string>();
  const params = useParams();
  const { mutate } = useAddAndRemoveTalent();
  const queryClient = useQueryClient();
  const id = params.id;
  const { token } = useGetUserInfo();
  const [folderFilter, setFolderFilter] = useState(false);

  const getCurrentJob = (workHistories: any[]) => {
    const currentJob = workHistories?.find((job) => job.is_current === 1);
    return currentJob;
  };

  const { data, isLoading } = useGetSingleFolder({
    id: id as unknown as number,
    location_country: filterFolderState.location_country,
    location_city: filterFolderState.location_city,
    title: filterFolderState.title,
    gender: filterFolderState.gender,
    visibility_score: filterFolderState.visibility_score,
    skills: filterFolderState.skills,
    experience_level: filterFolderState.experience_level,
    relocate: filterFolderState.relocate,
    token,
  });

  const formatExperienceLevel = (level: string): string => {
    return level
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const [searchQuery, setSearchQuery] = useState<string>("");

  const sendInvite = (id: string) => {
    setInviteModal(true);
    setTalentId(id);
  };

  const IdToBePassed = (data?: string): string[] => {
    if (selectedRowKeys.length > 0) {
      return selectedRowKeys;
    }
    if (!data) {
      return [];
    }
    return [data];
  };
  const handleSubmit = (data?: string) => {
    const props: IAddTalent = {
      token,
      talent_id: IdToBePassed(data),
      folder_id: id as unknown as number,
      request_url: "remove-talents-from-folder",
    };
    openNotification({
      state: "info",
      title: "Wait a second ...",
      description: "Loading...",
    });
    mutate(props, {
      onError: (err: any) => {
        let errorTitle = "Error";
        let errorDescription = "An error occurred, please try again.";

        if (err.response && err.response.data && err.response.data.error) {
          errorTitle = "Error: " + err.response.data.error;
          errorDescription =
            err.response.data.description || "Please try again.";
        }

        openNotification({
          title: errorTitle,
          state: "error",
          description: errorDescription,
          duration: 6,
        });
      },
      onSuccess: (res: any) => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Talent removed from folder successfully",
          duration: 4.5,
        });
        queryClient.invalidateQueries([QUERY_KEY_FOR_SINGLE_FOLDER]);
        setSelectedRowKeys([]);
      },
    });
  };

  const hasSelected = selectedRowKeys.length > 0;

  const filteredData = Array.isArray(data?.talents)
    ? data?.talents.filter((talent) =>
        `${talent.first_name} ${talent.last_name} ${talent.middle_name}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      )
    : [];

  const columns: ColumnsType<talentList> = [
    {
      title: "Talent Name",
      dataIndex: "name",
      render: (_, val) => (
        <h2 className="font-medium">
          {val?.first_name} {val?.last_name} {val?.middle_name}
        </h2>
      ),
    },
    {
      title: "Experience Level",
      dataIndex: "experience_level",
      render: (level) => <span>{formatExperienceLevel(level ?? "")}</span>,
    },
    {
      title: "Current Location",
      dataIndex: "current_location",
      render: (location) => <span>{location}</span>,
    },
    {
      title: "Job Title",
      dataIndex: "current_position",
      render: (_, val) => (
        <span>{getCurrentJob(val?.work_histories)?.title ?? null}</span>
      ),
    },
    {
      title: "Skills",
      dataIndex: "skills",
      render: (skills) => {
        let parsedItems: string[] = [];

        if (Array.isArray(skills)) {
          parsedItems = skills;
          return <span>{parsedItems.join(", ")}</span>;
        } else if (typeof skills === "string") {
          parsedItems = skills.trim().startsWith("[")
            ? JSON.parse(skills)
            : skills.split(",").map((s) => s.trim());
          return <span>{parsedItems.join(", ")}</span>;
        } else {
          return <span>No skills listed</span>;
        }
      },
    },
    {
      title: "Avatar",
      dataIndex: "avatar",
      render: (_, val) => (
        <div>
          <img
            src={val?.dp ? val?.dp : userDp}
            alt="avatar"
            className="rounded-full h-10 w-10 object-cover object-top"
          />
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",

      render: (_, val) => (
        <div>
          <Dropdown
            disabled={selectedRowKeys.length > 0}
            trigger={["click"]}
            overlay={
              <Menu>
                <Menu.Item key="1">
                  <Link to={routePath.talentDetails(val.id).path}>
                    View profile
                  </Link>
                </Menu.Item>
                <Menu.Item key="2">
                  <Popconfirm
                    okButtonProps={{
                      style: { backgroundColor: "#f5222d", color: "#ffffff" }, // Custom styles
                    }}
                    title="Remove Talent"
                    description="Are you sure to delete this talent?"
                    onConfirm={() => handleSubmit(val.id)}
                    okText="Yes"
                    cancelText="No"
                  >
                    Remove from folder
                  </Popconfirm>
                </Menu.Item>
                <Menu.Item key="3" onClick={() => sendInvite(val.id)}>
                  Send meeting invite
                </Menu.Item>
              </Menu>
            }
          >
            <i className="ri-more-2-fill text-lg cursor-pointer"></i>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <SendInvite
        id={talentId}
        open={inviteModal}
        handleClose={() => setInviteModal(false)}
      />
      <FilterFolder
        open={folderFilter}
        handleClose={() => setFolderFilter(false)}
      />
      <Navbar
        title={isLoading ? "Loading.." : `${data?.name} folder`}
        description={isLoading ? "Loading.." : `${data?.name} folder details`}
      />
      <div className="bg-gray-100 px-4 py-3 mt-8 rounded-md flex justify-between items-center">
        <button
          className="flex items-center gap-x-2"
        >
        </button>

        <button
          className="transparentButton flex items-center gap-x-2"
          style={{ background: "white" }}
          onClick={() => setFolderFilter(true)}
        >
          <i className="ri-filter-3-line text-base"></i>
          <span>Filters</span>
        </button>
      </div>

      <div className="bg-gray-100 px-4 py-3 mt-8 rounded-md">
        <div className="flex gap-3 flex-col md:flex-row md:justify-between md:items-center">
          <div className="flex items-center">
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none ">
                <i className="ri-search-line text-xl text-gray-500"></i>
              </div>
              <input
                type="search"
                className="border focus:outline-none border-gray-300 text-gray-900 text-sm rounded-lg block w-full pl-10 p-2.5 pr-24"
                placeholder="Search Talents"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      {hasSelected && (
        <div className="mt-7 flex items-center gap-3">
          <Popconfirm
            okButtonProps={{
              style: { backgroundColor: "#f5222d", color: "#ffffff" }, // Custom styles
            }}
            title={
              IdToBePassed().length > 1 ? "Remove Talents" : "Remove Talent"
            }
            description={
              IdToBePassed().length > 1
                ? "Are you sure to delete these talents?"
                : "Are you sure to delete this talent?"
            }
            onConfirm={() => handleSubmit()}
            okText="Yes"
            cancelText="No"
          >
            <button className="button" style={{ background: "#D92D20" }}>
              Remove from folder
            </button>
          </Popconfirm>
        </div>
      )}

      <Table
        className="bg-white rounded-md shadow border mt-8"
        columns={columns}
        dataSource={
          searchQuery
            ? filteredData
            : data?.talents?.map((item) => ({ ...item, key: item.id }))
        }
        rowSelection={{
          selectedRowKeys,
          onChange: (newSelectedRowKeys: React.Key[], rows) => {
            setSelectedRowKeys(rows.map((row) => row.id));
          },
        }}
        scroll={{ x: 550 }}
        loading={isLoading}
      />
    </DashboardLayout>
  );
};
